import { useMemo, useState } from 'react';
import { usePermissions } from '@/features/auth';
import { buildDealerNameMap, useDealersByIds } from '@/features/dealers/hooks/useDealersByIds';
import { DOCUMENT_TYPE_LABELS } from '../utils/documentTypeLabels';
import { buildProductSlots, dealerAssignmentRows, dealerIdsOf } from '../utils/assignmentSlots';
import { toTemplateOptions, truncationNotice } from '../utils/templateOptions';
import { UNKNOWN_DEALER_LABEL } from '../utils/templateOwnership';
import type { TemplateAssignment } from '../types/templateAssignments.types';
import type { AssignableDocumentType } from '../types/documentTemplates.types';
import { useAssignTemplateForm, type AssignTarget } from './useAssignTemplateForm';
import { useAssignableTemplatesQuery } from './useAssignableTemplatesQuery';
import { ASSIGNMENTS_PAGE_SIZE, useTemplateAssignmentsQuery } from './useTemplateAssignmentsQuery';
import { useUnassignTemplateMutation } from './useTemplateAssignmentMutations';
import { useConfirmedAction } from './useConfirmedAction';

const pickerDocumentType = (
  target: AssignTarget | null,
  chosen: { documentType: string; dealerId: string },
): AssignableDocumentType | null => {
  if (target?.kind === 'product-slot') return target.documentType;
  if (target?.kind === 'change') return target.assignment.documentType;
  if (target?.kind === 'dealer-override' && chosen.documentType && chosen.dealerId) {
    return chosen.documentType as AssignableDocumentType;
  }
  return null;
};

const pickerStatus = (documentType: AssignableDocumentType | null, isFetching: boolean) => {
  if (documentType === null) return 'idle' as const;
  return isFetching ? ('loading' as const) : ('ready' as const);
};

const pickerDealerId = (target: AssignTarget | null, chosen: string): string | null => {
  if (target?.kind === 'change') return target.assignment.dealerId;
  return target?.kind === 'dealer-override' && chosen ? chosen : null;
};

export function useProductDocumentsPanel(productId: string) {
  const { hasPermission } = usePermissions();
  const assignmentsQuery = useTemplateAssignmentsQuery(productId);
  const assignments = useMemo(() => assignmentsQuery.data?.assignments ?? [], [assignmentsQuery.data]);
  const canBrowseDealers = hasPermission({ resource: 'dealer', action: 'browse' });
  const dealersQuery = useDealersByIds(dealerIdsOf(assignments), canBrowseDealers);
  const dealerNames = useMemo(() => buildDealerNameMap(dealersQuery.data), [dealersQuery.data]);

  const [target, setTarget] = useState<AssignTarget | null>(null);
  const [unassigning, setUnassigning] = useState<TemplateAssignment | null>(null);
  const form = useAssignTemplateForm(productId, target, () => setTarget(null));
  const pickerType = pickerDocumentType(target, form.values);
  const templatesQuery = useAssignableTemplatesQuery(pickerType, pickerDealerId(target, form.values.dealerId));
  const unassign = useConfirmedAction(useUnassignTemplateMutation(), () => setUnassigning(null));

  const closeUnassign = () => {
    unassign.close();
    setUnassigning(null);
  };

  const total = assignmentsQuery.data?.meta.total ?? 0;
  const templateTotal = templatesQuery.data?.meta.total ?? 0;

  return {
    status: assignmentsQuery.isLoading ? ('loading' as const) : assignmentsQuery.isError ? ('error' as const) : ('ready' as const),
    errorMessage: assignmentsQuery.error ? (assignmentsQuery.error as Error).message : undefined,
    truncationNotice: truncationNotice(ASSIGNMENTS_PAGE_SIZE, total, 'assignments'),
    slots: buildProductSlots(assignments),
    dealerAssignments: dealerAssignmentRows(assignments, dealerNames),
    dealerNames,
    isResolvingDealers: dealersQuery.isLoading,
    abilities: {
      canAdd: hasPermission({ resource: 'document_assignment', action: 'add' }),
      canEdit: hasPermission({ resource: 'document_assignment', action: 'edit' }),
      canDelete: hasPermission({ resource: 'document_assignment', action: 'delete' }),
    },
    callbacks: {
      onAssignProductSlot: (documentType: AssignableDocumentType) => setTarget({ kind: 'product-slot', documentType }),
      onAddOverride: () => setTarget({ kind: 'dealer-override' }),
      onChange: (assignment: TemplateAssignment) => setTarget({ kind: 'change', assignment }),
      onUnassign: (assignment: TemplateAssignment) => {
        setUnassigning(assignment);
        unassign.open();
      },
    },
    modal: {
      isOpen: target !== null,
      form,
      target,
      templateOptions: toTemplateOptions(templatesQuery.data?.templates ?? []),
      templatesStatus: pickerStatus(pickerType, templatesQuery.isFetching),
      templatesNotice: truncationNotice(templatesQuery.data?.templates.length ?? 0, templateTotal, 'templates'),
      close: () => setTarget(null),
    },
    unassign: {
      isOpen: unassigning !== null && unassign.isOpen,
      assignment: unassigning,
      documentLabel: unassigning ? DOCUMENT_TYPE_LABELS[unassigning.documentType] : '',
      dealerName: unassigning?.dealerId ? (dealerNames.get(unassigning.dealerId) ?? UNKNOWN_DEALER_LABEL) : undefined,
      isPending: unassign.isPending,
      errorMessage: unassign.error ?? undefined,
      confirm: () => unassigning && unassign.confirm(unassigning.id),
      close: closeUnassign,
    },
  };
}
